import React, { useState, useEffect, useRef } from 'react';
import { api } from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { BellIcon } from './common/InlineIcons';

function timeAgo(date) {
  const seconds = Math.floor((Date.now() - new Date(date)) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function NotificationInbox({ open, onClose }) {
  const { VB } = useTheme();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [markingAll, setMarkingAll] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError('');
    api.get('/notifications')
      .then(r => setNotifications(Array.isArray(r.data) ? r.data : []))
      .catch(() => setError('Could not load notifications.'))
      .finally(() => setLoading(false));
  }, [open]);

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch {
      // silently fail
    } finally {
      setMarkingAll(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
    } catch {
      // silently fail
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 199 }}
        onClick={onClose}
      />
      {/* Panel */}
      <div
        ref={panelRef}
        style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          right: 0,
          width: 340,
          maxHeight: 480,
          background: VB.darkCard,
          borderRadius: 14,
          boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
          border: `1px solid ${VB.borderDark}`,
          zIndex: 200,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 16px', borderBottom: `1px solid ${VB.borderDark}`, flexShrink: 0,
        }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: VB.snowPrimary }}>
            Notifications
            {unreadCount > 0 && (
              <span style={{
                marginLeft: 8, background: VB.red, color: '#fff',
                fontSize: 11, fontWeight: 800, padding: '2px 7px',
                borderRadius: 10, verticalAlign: 'middle',
              }}>
                {unreadCount}
              </span>
            )}
          </span>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              disabled={markingAll}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: VB.gold, fontSize: 12, fontWeight: 600,
                opacity: markingAll ? 0.6 : 1,
              }}
            >
              Mark all read
            </button>
          )}
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading && (
            <div style={{ padding: '32px 16px', textAlign: 'center', color: VB.snowMuted, fontSize: 13 }}>
              Loading...
            </div>
          )}
          {error && !loading && (
            <div style={{ padding: '32px 16px', textAlign: 'center', color: VB.red, fontSize: 13 }}>
              {error}
            </div>
          )}
          {!loading && !error && notifications.length === 0 && (
            <div style={{ padding: '40px 16px', textAlign: 'center' }}>
              <div style={{ color: VB.red, display: 'flex', justifyContent: 'center', marginBottom: 8 }}><BellIcon size={28} strokeWidth={2} /></div>
              <div style={{ color: VB.snowMuted, fontSize: 13 }}>No notifications yet</div>
            </div>
          )}
          {!loading && !error && notifications.map(n => (
            <div
              key={n._id}
              onClick={() => !n.isRead && handleMarkRead(n._id)}
              style={{
                padding: '12px 16px',
                borderBottom: `1px solid ${VB.borderDark}`,
                background: n.isRead ? 'transparent' : 'rgba(233,162,59,0.05)',
                cursor: n.isRead ? 'default' : 'pointer',
                transition: 'background 0.15s',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: n.isRead ? 500 : 700,
                    color: VB.snowPrimary, marginBottom: 3,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {n.title}
                  </div>
                  <div style={{ fontSize: 12, color: VB.snowMuted, lineHeight: 1.4 }}>
                    {n.message || n.body}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                  <span style={{ fontSize: 11, color: VB.snowMuted }}>
                    {timeAgo(n.createdAt)}
                  </span>
                  {!n.isRead && (
                    <span style={{
                      width: 7, height: 7, borderRadius: '50%',
                      background: VB.red, display: 'inline-block',
                    }} />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
